/**
 * @lodex-pro/web-portal — entry point.
 *
 * Phase 4 / Plan 04-10 — bootstraps Amplify v6 BEFORE the React tree mounts so
 * `cognitoUserPoolsTokenProvider.setKeyValueStorage(...)` is wired before any
 * call to `signIn`, `confirmSignIn`, `fetchAuthSession`, etc.
 *
 * Phase 16 / DEC-159 — pool selection is now URL-path-based (v2 of the
 * "hostname-based discriminator" TODO left in the original Phase 4 comment).
 * `pickTenantTypeFromPath` parses `window.location.pathname` at module scope
 * BEFORE `bootstrapWebAmplify` because Amplify.configure is a module-level
 * singleton — one pool per page load. React-router's location context does
 * not exist yet at module load, so we cannot use `useLocation()` for this;
 * `window.location.pathname` is the only source available.
 *
 *   /portal/disposal/*  → disposal Cognito pool
 *   /portal/operator/*  → operator Cognito pool
 *   /portal/sign-in     → operator pool (LEGACY alias — 15.2 approval emails
 *                         and existing bookmarks point at this URL)
 *   everything else     → operator pool (safe default; disposal users hit
 *                         the pool selection from the 16.2/16.3 SES
 *                         `/portal/disposal/sign-in` URL)
 *
 * `pickTenantTypeFromPath` is a NAMED EXPORT so it is unit-testable in isolation
 * (`test/main.tsx.test.tsx`) — the SPA bootstrap itself is opaque to Vitest.
 *
 * Pools come from `VITE_COGNITO_POOL_CONFIG_JSON` (4 entries in CDK iteration
 * order — hauler, operator, disposal, platform).
 *
 * Phase 15 followup (DEC-152): added QueryProvider + ApiOptsProvider so the
 * portal home can fetch /v1/operator-portal/me via useApiQuery.
 *
 * Spec: CONTEXT D-03, D-05, D-07; RESEARCH § Pattern 4; DEC-159.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { bootstrapWebAmplify, QueryProvider, ApiOptsProvider } from '@lodex-pro/web-shared';
import type { AmplifyTenantType, PoolConfigEntry } from '@lodex-pro/shared/auth/amplify-config';

import { App } from './App';
import { resumeViewAs } from './auth/view-as';
// Tailwind v4 CSS-first entry + shadcn tokens + the auth-card skin. See
// src/styles.css (superset landed with the web-portal stylesheet) for the
// semantic-color mapping the disposal BI panels (DEC-163) consume.
import './styles.css';

/**
 * URL-path discriminator for Cognito pool selection (DEC-159).
 *
 * MUST be a pure function of `pathname` — no `window` / `document` / other
 * side inputs — so `test/main.tsx.test.tsx` can exercise every branch in
 * isolation. The bootstrap block below is the ONLY production caller and it
 * feeds `window.location.pathname` directly.
 *
 * Return type is narrowed to the two values web-portal supports
 * (`'operator' | 'disposal'`), but the compile-time type is `AmplifyTenantType`
 * because that is what `bootstrapWebAmplify` expects. `hauler` (mobile) and
 * `platform` (web-admin) are never returned from here.
 */
export function pickTenantTypeFromPath(pathname: string): AmplifyTenantType {
  if (pathname.startsWith('/portal/disposal')) {
    return 'disposal';
  }
  // Everything else — including /portal/operator/*, the LEGACY /portal/sign-in
  // alias, /portal/register, /portal/verify, and the /portal catch-all —
  // resolves to the operator pool. This preserves every 15.2/16.1 operator
  // URL contract while the 16.2/16.3 SES templates opt into the disposal
  // subtree by shipping `/portal/disposal/sign-in`.
  return 'operator';
}

const POOLS_RAW = (import.meta.env['VITE_COGNITO_POOL_CONFIG_JSON'] as string | undefined) ?? '[]';
const API_BASE_URL =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:3000';
const pools = JSON.parse(POOLS_RAW) as ReadonlyArray<PoolConfigEntry>;

const tenantType = pickTenantTypeFromPath(window.location.pathname);

bootstrapWebAmplify({
  pools,
  tenantType,
  apiBaseUrl: API_BASE_URL,
});

// Operator portal rework PR 5 — a reload keeps a view-as session this tab already holds.
resumeViewAs();

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element #root not found in index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryProvider>
        <ApiOptsProvider opts={{ baseUrl: API_BASE_URL }}>
          <App />
        </ApiOptsProvider>
      </QueryProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
